import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { FormField } from "@/components/ui/form-field";
import { TimeRangeRows } from "../components/TimeRangeRows";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DayRangeField } from "../components/DayRangeField";
import type { DayAxis } from "../models/day-axis";
import { UploadMultiImages } from "@/components/ui/upload-multi-images";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { Link, MapPin, Euro, Clock, CalendarRange, FileText, Image as ImageIcon, BedDouble, Library, AlertTriangle, Search, Check } from "lucide-react";
import { AccommodationActivity } from "../models/activity.model";
import { PRICE_TAG_OPTIONS, priceTagKey } from "../models/price-tag";
import { countGuestsFromRateHawkData } from "../ratehawk/ratehawk-accommodation/helpers";
import { getCountryByCode } from "@/constants/countries";
import type { IGuest } from "@/lib/request-trip/models/travel-request.model";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";
import ActivityDetailView from "./ActivityDetailView";
import { LibraryPicker } from "../components/LibraryPicker";
import type { LibraryActivity } from "@/lib/library/models/library-activity.model";

type AccommodationTab = 'details' | 'media' | 'documents' | 'library' | 'portal';


interface AccommodationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (activity: Partial<AccommodationActivity>) => void;
    icon?: React.ReactNode;
    initialData?: Partial<AccommodationActivity>;
    // optional itinerary bounds (may be provided by caller)
    itineraryStartDate?: string | null;
    itineraryEndDate?: string | null;
    /** Asse dei giorni: sui template si sceglie "Giorno N" invece di una data. */
    axis?: DayAxis | null;
    itineraryId: string;
    defaultDate?: string;
    /** Nasconde la card data e la rende non obbligatoria (usato dalla Libreria). */
    hideDates?: boolean;
    /** Sola lettura: campi non interagibili, footer con solo "Chiudi" (usato dalla Libreria). */
    readOnly?: boolean;
    /** Ospiti del viaggio: servono a proporre camere e occupanti alla ricerca sul portale. */
    guestsData?: IGuest[];
    totalGuests?: number;
    /** Provenienza del viaggiatore dalla travel request (ISO alpha-2 minuscolo). */
    residency?: string;
    /** Apre la ricerca RateHawk. Assente quando il portale non è disponibile. */
    onSearchPortal?: () => void;
  /** Il cliente ha gia' pagato: costo e "includi nel prezzo" sono consolidati. */
}

const AccommodationModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryStartDate, itineraryEndDate, axis, itineraryId, defaultDate, hideDates = false, readOnly = false, guestsData, totalGuests, residency, onSearchPortal }: AccommodationModalProps) => {
    const { t } = useI18n();

    // Generate temporary UUID for new activities
    const [activityId] = useState<string>(() =>
        initialData?.id || crypto.randomUUID()
    );

    const [formData, setFormData] = useState<Partial<AccommodationActivity>>({
        id: activityId,
        include_price: true,
        title: '',
        link: '',
        address: '',
        start_date: undefined,
        end_date: undefined,
        check_in_time: '',
        check_out_time: '',
        check_in_note: '',
        cost: 0,
        refundable_rate: false,
        price_tag: '',
        description: '',
        media: [],
        documentation: []
    });

    // Update form data when initialData changes
    useEffect(() => {
        if (initialData) {
            setFormData({ ...initialData, id: activityId });
        }
    }, [initialData, activityId]);

    const [errors, setErrors] = useState<Record<string, boolean>>({});
    // Import da libreria in due tempi: si sceglie, si conferma, poi si torna ai dati.
    const [pendingImport, setPendingImport] = useState<LibraryActivity | null>(null);
    const [showValidationBanner, setShowValidationBanner] = useState(false);
    const [activeTab, setActiveTab] = useState<AccommodationTab>('details');

    useEffect(() => {
        if (!isOpen) {
            setShowValidationBanner(false);
            setErrors({});
            setActiveTab('details');
            setPendingImport(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && !initialData && defaultDate) {
            setFormData(prev => ({ ...prev, start_date: defaultDate }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // I valori restano in inglese: sono quelli salvati su price_tag e quelli che
    // arrivano da RateHawk. Qui si traduce solo l'etichetta.
    const priceTagOptions = PRICE_TAG_OPTIONS;

    const validateForm = (): Record<string, boolean> => {
        const requiredFields = hideDates
            ? ['title', 'address', 'check_in_time', 'check_out_time', 'cost', 'price_tag', 'description']
            : ['title', 'address', 'start_date', 'end_date', 'check_in_time', 'check_out_time', 'cost', 'price_tag', 'description'];
        const newErrors: Record<string, boolean> = {};

        requiredFields.forEach(field => {
            const value = formData[field as keyof AccommodationActivity];
            if (field === 'cost') {
                newErrors[field] = value === null || value === undefined || (value as number) < 0;
            } else {
                newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
            }
        });

        setErrors(newErrors);
        return newErrors;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const newErrors = validateForm();
        if (!Object.values(newErrors).some(error => error)) {
            onSave({
                ...formData,
                type: 'accommodation' as const
            });
            onClose();
            return;
        }

        setShowValidationBanner(true);
            setActiveTab('details');
    };

    // derive min/max for date picker: prefer explicit props, then common fields on initialData
    const derivedMinDate: string | null =
        itineraryStartDate
        || (initialData as any)?.itineraryStartDate
        || (initialData as any)?.itinerary_start_date
        || (initialData as any)?.min_date
        || (initialData as any)?.minDate
        || null;

    const derivedMaxDate: string | null =
        itineraryEndDate
        || (initialData as any)?.itineraryEndDate
        || (initialData as any)?.itinerary_end_date
        || (initialData as any)?.max_date
        || (initialData as any)?.maxDate
        || null;

    const hasDetailsError = ['title', 'address', 'start_date', 'end_date', 'check_in_time', 'check_out_time', 'cost', 'price_tag', 'description']
        .some(field => errors[field]);
    const requiredError = String(t('validation.fieldRequired'));

    // In modifica non si importa: il tab compare solo creando una nuova attività.
    const canImportFromLibrary = !initialData;

    // Riepilogo di sola lettura di cosa erediterebbe la ricerca sul portale.
    // Non si modifica nulla qui: serve a dire in anticipo cosa manca.
    const hasGuestData = (guestsData?.length || 0) > 0 || !!totalGuests;
    const occupancy = countGuestsFromRateHawkData(guestsData, totalGuests);
    // Una camera ogni due adulti, come proposta di partenza.
    const roomCount = Math.max(1, Math.ceil(occupancy.adults / 2));
    const residencyCountry = residency ? getCountryByCode(residency.toUpperCase()) : undefined;

    const confirmImport = () => {
        if (!pendingImport) return;
        const imported = pendingImport.toItineraryActivity(formData.start_date || defaultDate);
        // L'id resta quello di questa modale: l'import precompila un inserimento nuovo,
        // non porta con sé l'identità dell'attività di libreria.
        setFormData({ ...(imported as Partial<AccommodationActivity>), id: activityId });
        setPendingImport(null);
        setErrors({});
        setShowValidationBanner(false);
        setActiveTab('details');
    };

    if (readOnly) {
        return (
            <ActivityDetailView
                isOpen={isOpen}
                onClose={onClose}
                icon={icon}
                title={t('builder.activities.accommodation')}
                activity={formData}
            />
        );
    }

    return (
        <ActivityModalShell
            isOpen={isOpen}
            onClose={onClose}
            icon={icon}
            title={t('builder.activities.accommodation')}
            formId="accommodation-form"
            onSubmit={handleSubmit}
            showValidationBanner={showValidationBanner}
            activeTab={activeTab}
            onActiveTabChange={(value) => setActiveTab(value as AccommodationTab)}
            tabs={[
                {
                    key: 'details',
                    label: t('modals.tabDetails'),
                    icon: <BedDouble className="h-3.5 w-3.5 shrink-0" />,
                    hasError: hasDetailsError,
                    content: (
                        <div className="space-y-5">
                          {/* Prima riga: a sinistra chi e dove, a destra quanto costa. */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-4">
                              <FormField
                                  id="title"
                                  label={t('modals.accommodationTitle')}
                                  required
                                  error={errors.title ? requiredError : undefined}
                              >
                                  <Input
                                      id="title"
                                      value={formData.title || ''}
                                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                      placeholder={t('modals.accommodationTitle')}
                                      className="h-9"
                                      hasError={errors.title}
                                  />
                              </FormField>

                              <FormField
                                  id="address"
                                  label={t('modals.address')}
                                  icon={<MapPin className="h-3 w-3" />}
                                  required
                                  error={errors.address ? requiredError : undefined}
                              >
                                  <Input
                                      id="address"
                                      value={formData.address || ''}
                                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                                      placeholder={t('modals.address')}
                                      className="h-9"
                                      hasError={errors.address}
                                  />
                              </FormField>
                            </div>

                            <div className="space-y-4">
                              <FormField
                                  id="cost"
                                  label={t('modals.price')}
                                  icon={<Euro className="h-3 w-3" />}
                                  required
                                  hint={t('modals.costHint')}
                                  error={errors.cost ? requiredError : undefined}
                              >
                                  <Input
                                      id="cost"
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={formData.cost}
                                      onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                                      placeholder="0,00"
                                      className="h-9 tabular-nums"
                                      hasError={errors.cost}
                                  />
                              </FormField>

                          <div className="flex items-start justify-between gap-4 rounded-md bg-gray-50 px-3 py-2.5">
                              <div className="space-y-0.5">
                                  <p className="text-xs text-gray-700">{t('modals.includePrice')}</p>
                                  <p className="text-[11px] text-gray-500">{t('modals.includePriceHint')}</p>
                              </div>
                              <Switch
                                  checked={formData.include_price || false}
                                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
                              />
                          </div>
                            </div>
                          </div>

                            <FormField
                                label={t('modals.description')}
                                required
                                error={errors.description ? requiredError : undefined}
                            >
                                <RichTextEditor
                                    value={formData.description || ''}
                                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                                    placeholder={t('modals.describeAccommodation')}
                                    hasError={errors.description}
                                />
                            </FormField>

                            {/* Date, check-in/out e prezzo sulla stessa riga; su mobile vanno a capo. */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {!hideDates && (
                                    <FormField
                                        label={t('modals.dateRange')}
                                        icon={<CalendarRange className="h-3 w-3" />}
                                        required
                                        error={errors.start_date || errors.end_date ? requiredError : undefined}
                                    >
                                        <DayRangeField
                                            axis={axis}
                                            value={formData}
                                            onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                                            hasError={errors.start_date || errors.end_date}
                                            minDate={derivedMinDate || undefined}
                                            maxDate={derivedMaxDate || undefined}
                                            // Il check-out cade tipicamente il giorno dopo l'ultima notte.
                                            allowEndBeyondDuration
                                            variant="rows"
                                        />
                                    </FormField>
                                )}

                                <FormField
                                    label={t('modals.checkInCheckOut')}
                                    icon={<Clock className="h-3 w-3" />}
                                    required
                                    error={errors.check_in_time || errors.check_out_time ? requiredError : undefined}
                                >
                                    <TimeRangeRows
                                        rows={[
                                            { id: 'check_in_time', label: String(t('modals.checkIn')) },
                                            { id: 'check_out_time', label: String(t('modals.checkOut')) },
                                        ]}
                                        values={formData}
                                        onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                                    />
                                </FormField>

                            </div>

                            <FormField id="checkInNote" label={t('modals.detail.checkInNote')}>
                                <Textarea
                                    id="checkInNote"
                                    value={formData.check_in_note || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, check_in_note: e.target.value }))}
                                    placeholder={t('modals.shareCheckInDetails')}
                                    rows={3}
                                />
                            </FormField>

                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="refundableRate"
                                    checked={formData.refundable_rate || false}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, refundable_rate: !!checked }))}
                                />
                                <Label htmlFor="refundableRate" className="text-xs text-gray-700">
                                    {t('modals.refundableRate')}
                                </Label>
                            </div>

                            <FormField
                                label={t('modals.detail.priceTag')}
                                required
                                error={errors.price_tag ? requiredError : undefined}
                            >
                                <ToggleGroup
                                    type="single"
                                    value={formData.price_tag || ''}
                                    onValueChange={(value) => setFormData(prev => ({ ...prev, price_tag: (value as string) || '' }))}
                                    variant="outline"
                                    className="flex items-start justify-start flex-wrap gap-0"
                                    hasError={errors.price_tag}
                                >
                                    {priceTagOptions.map((tag, index) => (
                                        <ToggleGroupItem
                                            key={tag}
                                            value={tag}
                                            className={[
                                                "h-8 px-2.5 text-xs font-normal rounded-none first:ml-0",
                                                index === 0 ? "rounded-l-lg" : "",
                                                index === priceTagOptions.length - 1 ? "rounded-r-lg" : "",
                                            ].join(" ")}
                                        >
                                            {priceTagKey(tag) ? t(priceTagKey(tag)!) : tag}
                                        </ToggleGroupItem>
                                    ))}
                                </ToggleGroup>
                            </FormField>

                        </div>
                    ),
                },
                {
                    key: 'media',
                    optional: true,
                    label: t('modals.tabMedia'),
                    icon: <ImageIcon className="h-3.5 w-3.5 shrink-0" />,
                    hasContent: (formData.media?.length || 0) > 0,
                    content: (
                        <FormField
                            label={t('modals.media')}
                            hint={t('modals.mediaHint')}
                        >
                            <UploadMultiImages
                                images={formData.media || []}
                                onImagesChange={(images) => setFormData(prev => ({ ...prev, media: images }))}
                                label={t('modals.upload4Photos')}
                                maxImages={10}
                                itineraryId={itineraryId}
                                activityId={activityId}
                            />
                        </FormField>
                    ),
                },
                {
                    key: 'documents',
                    optional: true,
                    label: t('modals.tabDocumentsAndLinks'),
                    icon: <FileText className="h-3.5 w-3.5 shrink-0" />,
                    hasContent: (formData.documentation?.length || 0) > 0 || !!formData.link,
                    content: (
                        <div className="space-y-5">
                          <FormField label={t('modals.documentation')} hint={t('modals.documentationHint')}>
                              <UploadMultiFiles
                                  files={formData.documentation || []}
                                  onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                                  label={t('modals.uploadBookingDocumentation')}
                                  maxFiles={5}
                                  itineraryId={itineraryId}
                                  activityId={activityId}
                              />
                          </FormField>

                          <FormField
                              id="link"
                              label={t('modals.link')}
                              icon={<Link className="h-3 w-3" />}
                              hint={t('modals.addSupportingLink')}
                          >
                              <Input
                                  id="link"
                                  type="url"
                                  value={formData.link || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                                  placeholder="https://..."
                                  className="h-9"
                                  hasError={errors.link}
                              />
                          </FormField>
                        </div>
                    ),
                },
                  {
                    key: 'library',
                    group: 'import' as const,
                    optional: true,
                    hidden: !canImportFromLibrary,
                    label: t('library.tabImport'),
                    icon: <Library className="h-3.5 w-3.5 shrink-0" />,
                    content: (
                      <div className="space-y-4">
                        {pendingImport && (
                          <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                            <p className="flex items-center gap-2 text-xs font-medium text-amber-800">
                              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                              {t('library.importConfirmTitle', { title: pendingImport.title })}
                            </p>
                            <p className="text-[11px] text-amber-700">{t('library.importConfirmBody')}</p>
                            <div className="flex gap-2">
                              <Button type="button" size="sm" onClick={confirmImport}>
                                {t('library.importConfirmAction')}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setPendingImport(null)}
                              >
                                {t('common.cancel')}
                              </Button>
                            </div>
                          </div>
                        )}

                        <LibraryPicker
                          activityType="accommodation"
                          enabled={isOpen && activeTab === 'library'}
                          onSelect={setPendingImport}
                          listClassName="max-h-[40vh]"
                        />
                      </div>
                    ),
                  },
                  {
                    key: 'portal',
                    group: 'import' as const,
                    optional: true,
                    hidden: !onSearchPortal || !!initialData,
                    label: t('builder.portalTab'),
                    icon: <Search className="h-3.5 w-3.5 shrink-0" />,
                    content: (
                      <div className="space-y-4">
                        <p className="text-xs text-gray-500">{t('builder.portalIntro')}</p>

                        <div className="divide-y divide-gray-200 rounded-md border border-input">
                          <div className="flex items-start gap-2.5 p-3">
                            {hasGuestData
                              ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                              : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />}
                            <div className="min-w-0 space-y-0.5">
                              <p className="text-xs font-medium text-gray-700">{t('builder.portalRooms')}</p>
                              <p className="text-sm text-gray-900">
                                {occupancy.children > 0
                                  ? t('builder.portalRoomsChildren', { rooms: roomCount, adults: occupancy.adults, children: occupancy.children })
                                  : t('builder.portalRoomsValue', { rooms: roomCount, adults: occupancy.adults })}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {hasGuestData ? t('builder.portalFromRequest') : t('builder.portalRoomsMissing')}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-2.5 p-3">
                            {residencyCountry
                              ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                              : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />}
                            <div className="min-w-0 space-y-0.5">
                              <p className="text-xs font-medium text-gray-700">{t('builder.portalResidency')}</p>
                              <p className="text-sm text-gray-900">
                                {residencyCountry ? `${residencyCountry.flag} ${residencyCountry.name}` : '🇮🇹 Italia'}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {residencyCountry ? t('builder.portalResidencyHint') : t('builder.portalResidencyMissing')}
                              </p>
                            </div>
                          </div>
                        </div>

                        <Button type="button" className="w-full" onClick={onSearchPortal}>
                          <Search className="mr-2 h-4 w-4" />
                          {t('builder.portalAction')}
                        </Button>
                      </div>
                    ),
                  },
            ]}
        />
    );
};

export default AccommodationModal;
